import {
  Alert,
  Box,
  Button,
  InputGroup as ChakraInputGroup,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  IconButton,
  Input,
  InputRightElement,
  List,
  ListItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { CustomCollapse } from 'BrokerTsLib/src/components'
import SubmitDialog from 'BrokerTsLib/src/components/SubmitDialog'
import {
  CalcMinAmountHouseholdRequest,
  CalcMinAmountRealtyRequest,
  InitialGeneralParamsPropertyValues,
  InitialPolicyHolderValues,
  InitialPropertyValues,
  InsuranceParamsPropertyDto,
  PolicyHolderDto,
  Property,
  SecondaryRealtyDto,
} from 'BrokerTsLib/src/dtos'
import {
  Insurer,
  Risk,
  RiskVariant,
  Selection,
  SelectionType,
} from 'BrokerTsLib/src/enums'
import * as global from 'brokertslib/src/helpers/global'
import { useGetRiskVariants } from 'BrokerTsLib/src/hooks'
import { formatDate } from 'BrokerTsLib/src/Utils/formatPrice'
import {
  GeneralParamsValidationSchema,
  PolicyHolderPropertyValidationSchema,
} from 'BrokerTsLib/src/validation'
import { PropertyValidationSchema } from 'BrokerTsLib/src/validation/property/PropertyValidationSchema'
import _ from 'lodash'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
} from 'react-hook-form'
import { Link } from 'react-router-dom'
import * as yup from 'yup'

import { Datepicker } from '../../components/Datepicker'
import ErrorSummary from '../../components/ErrorSummary'
import { FormGroup } from '../../components/FormGroup'
import { FormLabel } from '../../components/FormLabel'
import {
  FormSection,
  FormSectionBody,
  FormSectionDescription,
  FormSectionHeader,
  FormSectionTitle,
} from '../../components/FormSection'
import { FormSpacer } from '../../components/FormSpacer'
import { InputGroup } from '../../components/InputGroup'
import { InsuranceCompanies } from '../../components/InsuranceCompanies'
import { MainContent } from '../../components/MainContent'
import { Panel } from '../../components/Panel'
import { RadioStack } from '../../components/RadioStack'
import RuianAddressSelector from '../../components/RuianAddressSelector'
import { SwitchControl } from '../../components/SwitchControl'
import {
  useFindKatUzemi,
  useInsurers,
  useRegions,
  useSelections,
} from '../../hooks'
import { useNameof } from '../../hooks/useNameof'
import {
  useCalcRecommendedAmountHousehold,
  useCalcRecommendedAmountRealty,
  useGetStep1PropertyWarnings,
} from '../../hooks/useWizard'
import { Calculate, Check, QuestionMark, Search, Trash } from '../../icons'
import { useInsuranceParamsContext } from '../../layouts/Wizard'
import { BCNOptions } from '../BCNOptions'
import { ContractReplacement } from '../carkulka/ContractReplacement'
import { CustomNumberInput } from '../CustomNumberInput'
import { FormButtons } from '../FormButtons'
import { HouseFloorSelector } from '../HouseFloorSelector'
import { ICOOptions } from '../ICOOptions'
import { InsureeName } from '../InsureeName'
import { InsureeType } from '../InsureeType'
import { PropertyInsuranceTypeSelector } from './PropertyInsuranceTypeSelector'

interface Step1FormProps {
  onSubmit: (values: InsuranceParamsPropertyDto) => Promise<any>
  data?: InsuranceParamsPropertyDto
}

export const schema = yup.object().shape({
  generalParams: GeneralParamsValidationSchema,
  policyHolder: PolicyHolderPropertyValidationSchema,
  property: PropertyValidationSchema,
})

const defaultValues: InsuranceParamsPropertyDto = {
  nextPage: 'next',
  generalParams: InitialGeneralParamsPropertyValues,
  policyHolder: InitialPolicyHolderValues,
  property: InitialPropertyValues,
}

function isPlainObject(value: any): value is object {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    !(value instanceof Date)
  )
}

function customMerge<T extends object>(objA: T, objB: T): T {
  const result = {} as any
  // reset formulare z react-hook-form (v7.50.1. udajne tento problem vznikl ve verzi 7.22.*) dela nejaky interni merge objektu a undefined nebo null hodnoty ignoruje a nechava ve formulari puvodni.
  // Proto zde projdu vsechny hodnoty formulare a pokud je nemam v default values, tak je nastavim na vychozi hodnotu ('' nebo false)

  for (const key in objA) {
    const currentKey = key as keyof T
    if (isPlainObject(objA[currentKey])) {
      result[currentKey] = customMerge(
        objA[currentKey] as object,
        (objB[currentKey] as object) || {},
      )
    } else if (typeof objA[currentKey] === 'boolean') {
      result[currentKey] = currentKey in objB ? objB[currentKey] : false
    } else {
      result[currentKey] = currentKey in objB ? objB[currentKey] : ''
    }
  }

  return result
}

export const Step1Form = ({
  onSubmit,
  data = defaultValues,
}: Step1FormProps) => {
  const [isSubmittingCurrent, setIsSubmittingCurrent] = useState(false)
  const [isSubmittingNext, setIsSubmittingNext] = useState(false)
  const { triggerNewInsurance, setTriggerNewInsurance, insuranceParamsId } =
    useInsuranceParamsContext()
  const [calcHouseholdAmountError, setCalcHouseholdAmountError] = useState('')
  const [calcRealtyAmountError, setCalcRealtyAmountError] = useState('')

  const {
    data: dataLiability,
    isLoading: isLoadingLiability,
    isFetching: isFetchingLiability,
  } = useGetRiskVariants(Risk.LIABILITY)
  const { data: dataPf } = useSelections(SelectionType.FrekvencePlatby)
  const { data: dataPt } = useSelections(SelectionType.ZpusobPlatby)
  const { data: dataVztah } = useSelections(
    SelectionType.VztahKPremetuPojisteni,
  )
  const { data: dataApparmentType } = useSelections(
    SelectionType.TypBytovehoDomu,
  )
  const { data: dataBasementArea } = useSelections(
    SelectionType.PlochaPodsklepení,
  )
  const { data: dataOwner } = useSelections(SelectionType.Vlastnictvi)
  const { data: dataBuildingType } = useSelections(
    SelectionType.DalsiNemovitost,
  )
  const { data: dataTypKonstrukce } = useSelections(SelectionType.TypKonstrukce)
  const { data: dataStav } = useSelections(SelectionType.Stav)
  const { data: dataDokonceni } = useSelections(
    SelectionType.DokonceniNemovitosti,
  )
  const { data: dataVybaveni } = useSelections(SelectionType.Vybaveni)
  const { data: dataStrechy } = useSelections(SelectionType.TypStrechy)
  const {
    data: allInsurers,
    isLoading: isLoadingInsurers,
    isFetching: isFetchingInsurers,
  } = useInsurers(false, null)
  const { mutateAsync: calcRecommendedHouseholdAmount } =
    useCalcRecommendedAmountHousehold()
  const { mutateAsync: calcRecommendedRealtyAmount } =
    useCalcRecommendedAmountRealty()
  const { data: dataWarnings } = useGetStep1PropertyWarnings()

  const nameof = useNameof<InsuranceParamsPropertyDto>()
  const phnameof = useNameof<PolicyHolderDto>(nameof((x) => x.policyHolder))
  const pnameof = useNameof<Property>(nameof((x) => x.property))

  const isContracted = !!data.generalParams.contractNumber

  const methods = useForm<InsuranceParamsPropertyDto>({
    defaultValues: data,
    resolver: yupResolver(schema) as any,
  })

  const [isSubmitError, setIsSubmitError] = useState(false)

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
    control,
    watch,
    setValue,
    getValues,
    setError,
    reset,
  } = methods

  // pro pripad kdyz se z K1 klidne na 'Nové pojištění', tak se musi formular resetnout. Jinak nedochazelo k rerenderu
  useEffect(() => {
    if (triggerNewInsurance) {
      const defaultVals = customMerge(getValues(), defaultValues)
      defaultVals.property.insurers = allInsurers?.data.map((x) => x.id) ?? [] // // v default values nejsou vsechny pojistovny

      reset(defaultVals)
    }

    setTriggerNewInsurance(false)
  }, [triggerNewInsurance, reset])

  const submitForm = handleSubmit((data) => {
    const values = _.cloneDeep(data)
    // Datum poslu jen jako date bez casu. To by chtelo vyresit asi nejak obecne. Kdyz se to posle i s casem, tak dochazi k casovemu posunu, coz znamena na serveru predchozi den
    values.generalParams.insuranceStartDate = formatDate(
      values.generalParams.insuranceStartDate,
    )
    values.policyHolder.person.birthday = formatDate(
      values.policyHolder.person.birthday,
    )
    if (values.policyHolder.insuredPerson)
      values.policyHolder.insuredPerson.birthday = formatDate(
        values.policyHolder.insuredPerson.birthday,
      )

    return onSubmit(values)
      .then(() => {
        setIsSubmitError(false)
        reset(values)

        if (values.nextPage == 'current') savedFormAlertOnOpen()
      })
      .catch((err) => {
        setIsSubmitError(true)

        if (
          !(err.response && err.response.status === 400 && err.response.data)
        ) {
          savedFormAlertOnOpen()
          return
        }

        const data = err.response.data

        // zobrazeni serverove validace
        Object.keys(data).forEach((x: any) => {
          setError(global.toCamelCase(x), { message: data[x].join(' ') })
        })
      })
  })

  const {
    isOpen: isSavedFormAlertOpen,
    onOpen: savedFormAlertOnOpen,
    onClose: savedFormAlertOnClose,
  } = useDisclosure()

  useEffect(() => {
    if (!isSubmitting) {
      setIsSubmittingCurrent(false)
      setIsSubmittingNext(false)
    }
  }, [isSubmitting])

  const phType = watch('policyHolder.person.type')
  const postCodeField = watch('property.data.address.postCode')
  const [validPsc, setValidPsc] = useState(false)
  const { data: citiesData } = useRegions(postCodeField, { enabled: validPsc })
  const garagePostCodeField = watch('property.data.garageAddress.postCode')
  const [validGaragePsc, setValidGaragePsc] = useState(false)
  const { data: garageCitiesData } = useRegions(garagePostCodeField, {
    enabled: validGaragePsc,
  })
  const garageCities = validGaragePsc
    ? (garageCitiesData?.data.filter((x) => (x.city?.length ?? 0) > 0) ?? [])
    : []
  const katUzemiField = watch('property.data.address.postCodeCu')
  const [validKatUzemiPostCode, setValidKatUzemiPostCode] = useState(false)
  const { data: katUzemiData } = useFindKatUzemi(katUzemiField, {
    enabled: validKatUzemiPostCode,
  })
  const katUzemi = validKatUzemiPostCode ? (katUzemiData?.data ?? []) : []
  const katUzemiGarageField = watch('property.data.garageAddress.postCodeCu')
  const [validKatUzemiGaragePostCode, setValidKatUzemiGaragePostCode] =
    useState(false)
  const { data: katUzemiGarageData } = useFindKatUzemi(katUzemiGarageField, {
    enabled: validKatUzemiGaragePostCode,
  })
  const katUzemiGarage = validKatUzemiGaragePostCode
    ? (katUzemiGarageData?.data ?? [])
    : []
  const propRelationId = watch('property.data.propertyRelationId')

  const pcError = errors.property?.data?.address?.postCode
  useEffect(() => {
    setValidPsc(postCodeField?.length == 5 && !pcError)
  }, [postCodeField, pcError])
  const pcErrorGarage = errors.property?.data?.garageAddress?.postCode
  useEffect(() => {
    setValidGaragePsc(garagePostCodeField?.length == 5 && !pcErrorGarage)
  }, [garagePostCodeField, pcErrorGarage])
  const pcErrorKatUzemi = errors.property?.data?.address?.postCodeCu
  useEffect(() => {
    setValidKatUzemiPostCode(katUzemiField?.length == 5 && !pcErrorKatUzemi)
  }, [katUzemiField, pcErrorKatUzemi])
  const pcErrorKatUzemiGarage = errors.property?.data?.garageAddress?.postCodeCu
  useEffect(() => {
    setValidKatUzemiGaragePostCode(
      katUzemiGarageField?.length == 5 && !pcErrorKatUzemiGarage,
    )
  }, [katUzemiGarageField, pcErrorKatUzemiGarage])

  const onlyLotNumberValue = watch('property.data.address.isCatastralNumber')
  const floorsAboveGroundValue = watch('property.data.realty.floorsCount')
  const houseWithBasementValue = watch('property.data.hasBasement')
  const householdNumberOfFloorsValue = watch(
    'property.data.household.floorsCount',
  )

  const selectedType = watch('property.type')
  const { isOpen: isOpenRealty } = useDisclosure({
    isOpen: !!selectedType && selectedType != RiskVariant.Household,
  })
  const { isOpen: isOpenHousehold } = useDisclosure({
    isOpen: !!selectedType && selectedType != RiskVariant.Realty,
  })
  const selectedRealtyType = watch('property.data.realtyTypeId')
  const { isOpen: isOpenApartment } = useDisclosure({
    isOpen:
      !!selectedRealtyType &&
      (selectedRealtyType == Selection.Byt ||
        selectedRealtyType == Selection.NajemniByt),
  })
  const { isOpen: isOpenApartmentOwned } = useDisclosure({
    isOpen: !!selectedRealtyType && selectedRealtyType == Selection.Byt,
  })
  const { isOpen: isOpenBasement } = useDisclosure({
    isOpen: houseWithBasementValue == true,
  })
  const roofType = watch('property.data.realty.roofType')
  const { isOpen: isOpenRoofSedlova } = useDisclosure({
    isOpen: roofType == Selection.StrechaSedlova,
  })
  const residentialRoof = watch('property.data.realty.isRoofResidential')
  const { isOpen: isOpenRoofNotResidential } = useDisclosure({
    isOpen: residentialRoof == false,
  })
  const householdRoofType = watch('property.data.household.roofType')
  const { isOpen: isOpenHouseholdRoofSedlova } = useDisclosure({
    isOpen: householdRoofType == Selection.StrechaSedlova,
  })
  const { isOpen: isOpenGarageAtOtherLocation } = useDisclosure({
    isOpen: watch('property.data.hasGarageOnDifferentLocation') == true,
  })
  const garageOnlyLotNumberValue = watch(
    'property.data.garageAddress.isCatastralNumber',
  )
  const insuredPersonSameAs = watch('policyHolder.insuredPersonSameAs')
  const insuredPersonType = watch('policyHolder.insuredPerson.type')
  const { isOpen: isOpenFlood } = useDisclosure({
    isOpen: watch('property.data.floodInsuranceRequired') == true,
  })
  const secondaryRealty = watch('property.data.realty.secondaryRealty')

  const {
    fields: fieldsSecondaryRealties,
    remove: removeSecondaryRealty,
    append: appendSecondaryRealty,
  } = useFieldArray({
    name: 'property.data.secondaryRealties',
    control: control,
  })

  const sameAsOptions = useMemo(
    () => [
      {
        label: 'Stejný s pojistníkem',
        value: Selection.PersonSameAsPolicyHolder,
      },
      { label: 'Jiný', value: null },
    ],
    [],
  )

  const isInsurersAvailable = useCallback(
    (insurers: Insurer[]) => {
      return (
        (allInsurers?.data.filter((x) => insurers.includes(x.id))?.length ??
          0) > 0
      )
    },
    [allInsurers?.data],
  )

  const isInsurerAvailable = useCallback(
    (insurer: Insurer) => {
      return isInsurersAvailable([insurer])
    },
    [isInsurersAvailable],
  )

  const isGarageAtOtherLocationAvailable =
    isInsurerAvailable(Insurer.CPP) || isInsurerAvailable(Insurer.Direct)
  const isGamageAtOtherLocationEquipmentAvailable = isInsurerAvailable(
    Insurer.Direct,
  )

  const warnings =
    dataWarnings?.data?.filter(
      (x) =>
        x.isAddressLocationCadastral == onlyLotNumberValue &&
        !!selectedRealtyType &&
        x.isRecreationalProperty ==
        (selectedRealtyType == Selection.RekreacniObjekt),
    ) ?? []

  useEffect(() => {
    if (secondaryRealty && fieldsSecondaryRealties.length == 0)
      appendSecondaryRealty({} as SecondaryRealtyDto)
  }, [secondaryRealty, fieldsSecondaryRealties.length])

  const calcHouseholdAmount = () => {
    const req: CalcMinAmountHouseholdRequest = {
      livingArea: getValues('property.data.household.livingArea'),
      equipment: getValues('property.data.household.equipment'),
    }
    calcRecommendedHouseholdAmount(req).then((resp) => {
      if (resp.data.amount) {
        setValue('property.data.household.amount', resp.data.amount!)
        setCalcHouseholdAmountError('')
      } else {
        setCalcHouseholdAmountError(resp.data.message)
      }
    })
  }

  const calcRealtyAmount = () => {
    const req: CalcMinAmountRealtyRequest = {
      builtUpArea: getValues('property.data.realty.builtUpArea'),
      builtUpCadastralArea: getValues(
        'property.data.realty.builtUpCadastralArea',
      ),
      usefulArea: getValues('property.data.realty.usefulArea'),
      realtyType: getValues('property.data.realtyTypeId'),
      appartmentType: getValues('property.data.realty.apparmentType'),
      hasBasement: getValues('property.data.hasBasement'),
      basementArea: getValues('property.data.realty.basementArea'),
      floorCount: getValues('property.data.realty.floorsCount'),
      roofType: getValues('property.data.realty.roofType'),
      isRoofResidential: getValues('property.data.realty.isRoofResidential'),
      finishState: getValues('property.data.realty.finishState'),
      structuralType: getValues('property.data.realty.structuralType'),
    }
    calcRecommendedRealtyAmount(req).then((resp) => {
      if (resp.data.amount) {
        setValue('property.data.realty.realtyAmount', resp.data.amount!)
        setCalcRealtyAmountError('')
      } else {
        setCalcRealtyAmountError(resp.data.message)
      }
    })
  }

  const minStartDate = new Date().customAddDays(1).setHours(0, 0, 0, 0)

  return (
    <FormProvider {...methods}>
      <form onSubmit={submitForm}>
        <MainContent>
          <FormSection>
            <FormSectionHeader>
              <FormSectionTitle>Parametry pojištění</FormSectionTitle>

              <FormSectionDescription>
                Zadejte základní parametry pojištění.
              </FormSectionDescription>
            </FormSectionHeader>

            <FormSectionBody>
              {/* Project name */}
              <FormControl isInvalid={!!errors.generalParams?.name}>
                <FormLabel
                  tooltip="Zadejte název nabídky"
                  htmlFor="projectName"
                >
                  Název nabídky
                </FormLabel>
                <InputGroup
                  id="projectName"
                  placeholder="Zadejte název nabídky"
                  htmlSize={32}
                  isDisabled={isContracted}
                  errors={errors.generalParams?.name}
                  {...register('generalParams.name', {
                    // required: 'Zadejte název nabídky',
                  })}
                />
              </FormControl>

              <FormSpacer />

              <ContractReplacement />

              <FormSpacer />

              <PropertyInsuranceTypeSelector />

              <FormSpacer />

              {/* Date */}
              <FormControl
                isInvalid={!!errors.generalParams?.insuranceStartDate}
              >
                <FormLabel
                  htmlFor="startDate"
                  tooltip={
                    <>
                      Počátek je možné nastavit nejdříve na následující den.
                      <br />
                      Allianz pojišťovna umožňuje sjednat pojištění s odloženým
                      počátkem až o 1 rok.
                      <br />
                      Česká podnikatelská pojišťovna umožňuje sjednat pojištění
                      s odloženým počátkem až o 1 rok.
                      <br />
                      ČSOB pojišťovna umožňuje sjednat pojištění až maximálně 3
                      měsíce dopředu.
                      <br />
                      Direct pojišťovna umožňuje sjednat pojištění až maximálně
                      6 měsíců dopředu.
                      <br />
                      Kooperativa pojišťovna umožňuje sjednat pojištění až
                      maximálně 1 rok dopředu.
                      <br />
                      Pillow pojišťovna umožňuje sjednat pojištění až maximálně
                      3 měsíce dopředu.
                      <br />
                      Slavia pojišťovna umožňuje sjednat pojištění až maximálně
                      9 měsíců dopředu.
                      <br />
                      UNIQA pojišťovna umožňuje sjednat pojištění až maximálně 1
                      rok dopředu.
                      <br />
                      Generali Česká pojišťovna umožňuje sjednat pojištění až
                      maximálně 9 měsíců dopředu.
                      <br />
                    </>
                  }
                >
                  Datum počátku pojištění
                </FormLabel>

                <Datepicker
                  isDisabled={isContracted}
                  errors={errors.generalParams?.insuranceStartDate}
                  date={getValues('generalParams.insuranceStartDate')}
                  onDateChange={(date) =>
                    setValue('generalParams.insuranceStartDate', date)
                  }
                  options={{
                    minDate: minStartDate,
                  }}
                />
              </FormControl>

              <FormSpacer />

              <FormGroup size="lg">
                <FormControl
                  isInvalid={!!errors.generalParams?.paymentFrequency}
                >
                  <FormLabel
                    htmlFor="paymentFrequency"
                    tooltip={
                      <>
                        Vyberte frekvenci plateb
                        <br />
                        Allianz umožňuje: roční, měsíční
                        <br />
                        ČPP umožňuje: roční, pololetní, čtvrtletní
                        <br />
                        ČSOB umožňuje: roční, pololetní, čtvrtletní
                        <br />
                        Direct umožňuje: roční, pololetní, čtvrtletní
                        <br />
                        GČP umožňuje: roční, pololetní, čtvrtletní, měsíční
                        <br />
                        Kooperativa umožňuje: roční, pololetní, čtvrtletní,
                        měsíční
                        <br />
                        Pillow umožňuje: roční, pololetní, čtvrtletní, měsíční
                        <br />
                        Slavia umožňuje: roční, pololetní, čtvrtletní
                        <br />
                        UNIQA umožňuje: roční, pololetní, čtvrtletní
                      </>
                    }
                  >
                    Frekvence plateb
                  </FormLabel>

                  <RadioGroup
                    isDisabled={isContracted}
                    onChange={(x) =>
                      setValue('generalParams.paymentFrequency', parseInt(x))
                    }
                    value={watch('generalParams.paymentFrequency')?.toString()}
                  >
                    <Stack direction="column" spacing={4}>
                      {dataPf?.data
                        .filter((item) => item.id != Selection.OneTime)
                        .map((item) => (
                          <Radio
                            value={item.id.toString()}
                            key={item.id.toString()}
                          >
                            {item.description}
                          </Radio>
                        ))}
                    </Stack>
                  </RadioGroup>

                  {errors.generalParams?.paymentFrequency && (
                    <FormErrorMessage>
                      {errors.generalParams?.paymentFrequency.message}
                    </FormErrorMessage>
                  )}
                </FormControl>

                <FormControl isInvalid={!!errors.generalParams?.paymentType}>
                  <FormLabel htmlFor="paymentMethod">Způsob platby</FormLabel>

                  <RadioGroup
                    isDisabled={isContracted}
                    onChange={(x) =>
                      setValue('generalParams.paymentType', parseInt(x))
                    }
                    value={watch('generalParams.paymentType')?.toString()}
                  >
                    <Stack direction="column" spacing={4}>
                      {dataPt?.data.map((item) => (
                        <Radio
                          value={item.id.toString()}
                          key={item.id.toString()}
                        >
                          {item.description}
                        </Radio>
                      ))}
                    </Stack>
                  </RadioGroup>

                  {errors.generalParams?.paymentType && (
                    <FormErrorMessage>
                      {errors.generalParams?.paymentType.message}
                    </FormErrorMessage>
                  )}
                </FormControl>
              </FormGroup>

              <FormSpacer />

              <FormControl isInvalid={!!errors.property?.range}>
                <FormLabel>Rozsah pojištění</FormLabel>

                <RadioGroup
                  onChange={(x) => setValue('property.range', parseInt(x))}
                  value={watch('property.range')?.toString()}
                  isDisabled={isContracted}
                >
                  <RadioStack variant="stackedTile">
                    <Radio value={RiskVariant.MiniRange.toString()}>
                      Nižší
                    </Radio>
                    <Radio value={RiskVariant.MaxiRange.toString()}>
                      Vyšší
                    </Radio>
                  </RadioStack>
                </RadioGroup>

                {errors.property?.range && (
                  <FormErrorMessage>
                    {errors.property?.range.message}
                  </FormErrorMessage>
                )}
              </FormControl>

              <FormSpacer />

              <FormControl isInvalid={!!errors.property?.insurers}>
                <InsuranceCompanies
                  companies={allInsurers?.data ?? []}
                  isLoadingCompanies={isFetchingInsurers || isLoadingInsurers}
                  propPath={pnameof((x) => x.insurers)}
                  isDisabled={isContracted}
                  tooltip="Vyberte pojišťovny, které chcete zahrnout do kalkulace pojištění."
                />

                {errors.property?.insurers && (
                  <FormErrorMessage>
                    {errors.property.insurers.message}
                  </FormErrorMessage>
                )}
              </FormControl>

              {warnings.length > 0 && (
                <Alert colorScheme="orange">
                  <Stack direction="column">
                    {warnings.map((item) => {
                      return (
                        <Text
                          key={item.message}
                          dangerouslySetInnerHTML={{ __html: item.message }}
                        ></Text>
                      )
                    })}
                  </Stack>
                </Alert>
              )}
            </FormSectionBody>
          </FormSection>

          <FormSection>
            <FormSectionHeader>
              <FormSectionTitle>Místo pojištění</FormSectionTitle>

              <FormSectionDescription>
                Zadejte základní údaje o nemovitosti.
              </FormSectionDescription>
            </FormSectionHeader>

            <FormSectionBody>
              <KNButton />

              <RuianAddressSelector
                addressPath={nameof((x) => x.property.data.address)}
                isDisabled={isContracted}
              />

              {isInsurerAvailable(
                Insurer.Allianz,
              ) /*watch('property.data.address.isOutsideUrbanAreaAvailable') == true*/ && (
                  <SwitchControl>
                    <Switch
                      id="outsideUrbanArea"
                      isDisabled={isContracted}
                      {...register('property.data.address.isOutsideUrbanArea')}
                      isChecked={watch(
                        'property.data.address.isOutsideUrbanArea',
                      )}
                    />
                    <FormLabel
                      htmlFor="outsideUrbanArea"
                      variant="switch"
                      tooltip="Pro pojišťovnu Allianz: místo pojištění se nachází mimo obec."
                    >
                      Místo se nachází mimo obec
                    </FormLabel>
                  </SwitchControl>
                )}

              <FormSpacer />

              <CustomCollapse in={!!selectedRealtyType && !isOpenApartment}>
                <FormControl
                  isRequired
                  isInvalid={!!errors.property?.data?.hasBasement}
                >
                  <FormLabel>Je dům podsklepený</FormLabel>

                  <RadioGroup
                    isDisabled={isContracted}
                    value={
                      watch('property.data.hasBasement') == true
                        ? 'yes'
                        : watch('property.data.hasBasement') == false
                          ? 'no'
                          : undefined
                    }
                    onChange={(val) =>
                      setValue('property.data.hasBasement', val === 'yes')
                    }
                  >
                    <RadioStack variant="stackedTile">
                      <Radio value="yes">Ano</Radio>
                      <Radio value="no">Ne</Radio>
                    </RadioStack>
                  </RadioGroup>

                  {errors.property?.data?.hasBasement && (
                    <FormErrorMessage>
                      {errors.property?.data?.hasBasement.message}
                    </FormErrorMessage>
                  )}
                </FormControl>
              </CustomCollapse>

              <CustomCollapse in={isOpenApartment}>
                <FormControl
                  isInvalid={!!errors.property?.data?.hasNonresidentialPremises}
                  isRequired
                >
                  <FormLabel tooltip="Nejčastěji sklepní kóje nebo garáž.">
                    Nebytové prostory
                  </FormLabel>

                  <RadioGroup
                    isDisabled={isContracted}
                    value={
                      watch('property.data.hasNonresidentialPremises') == true
                        ? 'yes'
                        : watch('property.data.hasNonresidentialPremises') ==
                          false
                          ? 'no'
                          : undefined
                    }
                    onChange={(val) =>
                      setValue(
                        'property.data.hasNonresidentialPremises',
                        val === 'yes',
                      )
                    }
                  >
                    <RadioStack variant="stackedTile">
                      <Radio value="yes">Ano</Radio>
                      <Radio value="no">Ne</Radio>
                    </RadioStack>
                  </RadioGroup>

                  {errors.property?.data?.hasNonresidentialPremises && (
                    <FormErrorMessage>
                      {errors.property?.data?.hasNonresidentialPremises.message}
                    </FormErrorMessage>
                  )}
                </FormControl>
              </CustomCollapse>

              <CustomCollapse in={isGarageAtOtherLocationAvailable}>
                <FormControl
                  isRequired
                  isInvalid={
                    !!errors.property?.data?.hasGarageOnDifferentLocation
                  }
                >
                  <FormLabel tooltip="Garáž se nachází na jiné adrese než místo pojištění.">
                    Garáž na jiné adrese
                  </FormLabel>

                  <RadioGroup
                    isDisabled={isContracted}
                    value={
                      watch('property.data.hasGarageOnDifferentLocation') ==
                        true
                        ? 'yes'
                        : watch('property.data.hasGarageOnDifferentLocation') ==
                          false
                          ? 'no'
                          : undefined
                    }
                    onChange={(val) =>
                      setValue(
                        'property.data.hasGarageOnDifferentLocation',
                        val === 'yes',
                      )
                    }
                  >
                    <RadioStack variant="stackedTile">
                      <Radio value="yes">Ano</Radio>
                      <Radio value="no">Ne</Radio>
                    </RadioStack>
                  </RadioGroup>

                  {errors.property?.data?.hasGarageOnDifferentLocation && (
                    <FormErrorMessage>
                      {
                        errors.property?.data?.hasGarageOnDifferentLocation
                          .message
                      }
                    </FormErrorMessage>
                  )}
                </FormControl>
              </CustomCollapse>

              <CustomCollapse in={isOpenGarageAtOtherLocation}>
                <FormSpacer />

                <Box sx={{ display: 'grid', gap: 4 }}>
                  <FormControl
                    isInvalid={
                      !!errors.property?.data?.garageAtOtherLocationAmount
                    }
                    isRequired
                  >
                    <FormLabel tooltip="Pojistná částka budovy garáže">
                      Pojistná částka
                    </FormLabel>

                    <CustomNumberInput
                      w={48}
                      defaultValue={getValues(
                        'property.data.garageAtOtherLocationAmount',
                      )}
                      onValueChange={(_, value) =>
                        setValue(
                          'property.data.garageAtOtherLocationAmount',
                          value,
                        )
                      }
                      isDisabled={isContracted}
                      inputRightElement={'Kč'}
                      placeholder="Např. 1 500 000"
                    />

                    {errors.property?.data?.garageAtOtherLocationAmount && (
                      <FormErrorMessage>
                        {
                          errors.property?.data?.garageAtOtherLocationAmount
                            .message
                        }
                      </FormErrorMessage>
                    )}
                  </FormControl>

                  {isGamageAtOtherLocationEquipmentAvailable && (
                    <FormControl>
                      <FormLabel tooltip="Pojišťovna Direct umožňuje pojistit vybavení garáže">
                        Vybavení garáže
                      </FormLabel>

                      <CustomNumberInput
                        w={48}
                        defaultValue={getValues(
                          'property.data.garageAtOtherLocationEquipmentAmount',
                        )}
                        onValueChange={(_, value) =>
                          setValue(
                            'property.data.garageAtOtherLocationEquipmentAmount',
                            value,
                          )
                        }
                        isDisabled={isContracted}
                        inputRightElement={'Kč'}
                        placeholder="Např. 1 500 000"
                      />
                    </FormControl>
                  )}

                  <RuianAddressSelector
                    addressPath={nameof((x) => x.property.data.garageAddress)}
                    isDisabled={isContracted}
                  />

                  {watch(
                    'property.data.garageAddress.isOutsideUrbanAreaAvailable',
                  ) == true && (
                      <SwitchControl>
                        <Switch
                          id="garageOutsideUrbanArea"
                          {...register(
                            'property.data.garageAddress.isOutsideUrbanArea',
                          )}
                          isChecked={watch(
                            'property.data.garageAddress.isOutsideUrbanArea',
                          )}
                          isDisabled={isContracted}
                        />
                        <FormLabel
                          htmlFor="garageOutsideUrbanArea"
                          variant="switch"
                          tooltip="Pro pojišťovnu Allianz: místo pojištění se nachází mimo obec."
                        >
                          Místo se nachází mimo obec
                        </FormLabel>
                      </SwitchControl>
                    )}
                </Box>
              </CustomCollapse>
            </FormSectionBody>
          </FormSection>

          <CustomCollapse in={isOpenRealty}>
            <FormSection>
              <FormSectionHeader>
                <FormSectionTitle>Nemovitost</FormSectionTitle>

                <FormSectionDescription>Trvale obývaná.</FormSectionDescription>
              </FormSectionHeader>

              <FormSectionBody>
                <CustomCollapse in={isOpenApartmentOwned}>
                  <FormControl
                    isInvalid={!!errors.property?.data?.realty?.owner}
                    isRequired
                  >
                    <FormLabel
                      tooltip={
                        <>
                          Osobní vlastnictví bytu - pojištěný je zapsán jako
                          vlastník v katastru nemovitostí.
                          <br />
                          Družstevní vlastnictví bytu - vlastníkem družstevního
                          bytu je vždy družstvo (bytové, stavební bytové apod.),
                          které s družstevníkem, tedy nájemcem uzavírá nájemní
                          smlouvu. Některé pojišťovny umožňují takovému
                          nájemníkovi pojistit nejen domácnost, ale i stavbu -
                          bytovou jednotku. V takovém případě se jedná o
                          pojištění cizího pojistného nebezpečí. Vlastník musí
                          být s touto pojistnou smlouvou seznámen a případné
                          právo na pojistné plnění má jen v případě, prokáže-li,
                          že vlastníka bytu/jednotky, tzn. družstvo, se smlouvou
                          seznámil a družstvo jako vlastník pojištěné věci s
                          poskytnutím pojistného plnění vyjádřilo svůj souhlas.
                        </>
                      }
                    >
                      Vlastnictví
                    </FormLabel>

                    <RadioGroup
                      isDisabled={isContracted}
                      value={watch('property.data.realty.owner')?.toString()}
                      onChange={(x) =>
                        setValue('property.data.realty.owner', parseInt(x))
                      }
                    >
                      <RadioStack variant="stackedTile">
                        {dataOwner?.data.map((item) => (
                          <Radio
                            value={item.id.toString()}
                            key={item.id.toString()}
                          >
                            {item.description}
                          </Radio>
                        ))}
                      </RadioStack>
                    </RadioGroup>

                    {errors.property?.data?.realty?.owner && (
                      <FormErrorMessage>
                        {errors.property?.data?.realty?.owner.message}
                      </FormErrorMessage>
                    )}
                  </FormControl>
                </CustomCollapse>

                <CustomCollapse in={isOpenApartment}>
                  <FormControl
                    isInvalid={!!errors.property?.data?.realty?.apparmentType}
                    isRequired
                  >
                    <FormLabel tooltip="Typové bytové domy jsou domy realizované hromadnou výstavbou.">
                      Bytový dům
                    </FormLabel>

                    <RadioGroup
                      isDisabled={isContracted}
                      value={watch(
                        'property.data.realty.apparmentType',
                      )?.toString()}
                      onChange={(x) =>
                        setValue(
                          'property.data.realty.apparmentType',
                          parseInt(x),
                        )
                      }
                    >
                      <RadioStack variant="stackedTile">
                        {dataApparmentType?.data.map((item) => (
                          <Radio
                            value={item.id.toString()}
                            key={item.id.toString()}
                          >
                            {item.description}
                          </Radio>
                        ))}
                      </RadioStack>
                    </RadioGroup>

                    {errors.property?.data?.realty?.apparmentType && (
                      <FormErrorMessage>
                        {errors.property?.data?.realty?.apparmentType.message}
                      </FormErrorMessage>
                    )}
                  </FormControl>
                </CustomCollapse>

                <FormGroup alignItems="stretch">
                  <FormControl
                    isRequired
                    isInvalid={!!errors.property?.data?.realty?.isApproved}
                  >
                    <FormLabel tooltip="Zkolaudováno">Zkolaudováno</FormLabel>

                    <RadioGroup
                      isDisabled={isContracted}
                      value={
                        watch('property.data.realty.isApproved') == true
                          ? 'yes'
                          : watch('property.data.realty.isApproved') == false
                            ? 'no'
                            : undefined
                      }
                      onChange={(val) =>
                        setValue(
                          'property.data.realty.isApproved',
                          val === 'yes',
                        )
                      }
                    >
                      <RadioStack variant="stackedTile">
                        <Radio value="yes">Ano</Radio>
                        <Radio value="no">Ne</Radio>
                      </RadioStack>
                    </RadioGroup>

                    {errors.property?.data?.realty?.isApproved && (
                      <FormErrorMessage>
                        {errors.property?.data?.realty?.isApproved.message}
                      </FormErrorMessage>
                    )}
                  </FormControl>

                  <FormControl
                    isInvalid={!!errors.property?.data?.realty?.approvedYear}
                    isRequired
                  >
                    <FormLabel tooltip="Uveďte rok kolaudace/uvedení do provozu nebo rok poslední rekolaudace.">
                      Rok kolaudace či výstavby
                    </FormLabel>

                    <InputGroup
                      isDisabled={isContracted}
                      htmlSize={4}
                      {...register('property.data.realty.approvedYear')}
                      errors={errors.property?.data?.realty?.approvedYear}
                    />
                  </FormControl>
                </FormGroup>

                <FormSpacer />

                <FormGroup>
                  <FormControl
                    isRequired
                    isInvalid={!!errors.property?.data?.realty?.builtUpArea}
                  >
                    <FormLabel tooltip="Součet plochy všech podlaží, včetně balkonů, teras, sklepů a půdních prostor, bez ohledu na to, zda jsou využívány">
                      Celková podlahová plocha
                    </FormLabel>

                    <CustomNumberInput
                      w={32}
                      defaultValue={getValues(
                        'property.data.realty.builtUpArea',
                      )}
                      onValueChange={(_, value) =>
                        setValue('property.data.realty.builtUpArea', value)
                      }
                      isDisabled={isContracted}
                      inputRightElement="㎡"
                      placeholder="Např. 128"
                    />

                    {errors.property?.data?.realty?.builtUpArea && (
                      <FormErrorMessage>
                        {errors.property?.data?.realty?.builtUpArea.message}
                      </FormErrorMessage>
                    )}
                  </FormControl>

                  <FormControl>
                    <FormLabel tooltip="z toho určeno k podnikání">
                      z toho určeno k podnikání
                    </FormLabel>

                    <CustomNumberInput
                      w={32}
                      value={watch('property.data.realty.builtUpAreaBusiness')}
                      onValueChange={(_, value) =>
                        setValue(
                          'property.data.realty.builtUpAreaBusiness',
                          value,
                        )
                      }
                      isDisabled={isContracted}
                      inputRightElement="%"
                      placeholder="Např. 20"
                    />
                  </FormControl>
                </FormGroup>

                <FormGroup sx={{ alignItems: 'end' }}>
                  <FormControl
                    isRequired
                    isInvalid={
                      !!errors.property?.data?.realty?.builtUpCadastralArea
                    }
                  >
                    <FormLabel tooltip="Plocha ohraničená pravoúhlými průměty vnějšího líce obvodových konstrukcí všech nadzemních i podzemních podlaží do vodorovné roviny. Je uvedena v katastru nemovitostí.">
                      Zastavěná plocha
                    </FormLabel>

                    <CustomNumberInput
                      w={32}
                      defaultValue={getValues(
                        'property.data.realty.builtUpCadastralArea',
                      )}
                      onValueChange={(_, value) =>
                        setValue(
                          'property.data.realty.builtUpCadastralArea',
                          value,
                        )
                      }
                      isDisabled={isContracted}
                      inputRightElement="㎡"
                      placeholder="Např. 128"
                    />

                    {errors.property?.data?.realty?.builtUpCadastralArea && (
                      <FormErrorMessage>
                        {
                          errors.property?.data?.realty?.builtUpCadastralArea
                            .message
                        }
                      </FormErrorMessage>
                    )}
                  </FormControl>

                  {/* <Button
                    as="a"
                    href="https://intranet.okgroup.cz/okg-informace/13664"
                    target="_blank"
                    rel="noopener"
                    variant="outline"
                    isDisabled={isContracted}
                  >
                    Vyhledat v katastru nemovitostí <External color="secondary.500" />
                  </Button> */}
                </FormGroup>

                <CustomCollapse in={!isOpenApartment}>
                  <FormSpacer />
                  <Flex
                    sx={{
                      columnGap: 32,
                      rowGap: 8,
                      placeItems: 'start',
                      flexWrap: 'wrap',
                    }}
                  >
                    <Box sx={{ display: 'grid', gap: 4 }}>
                      <FormControl
                        isRequired
                        isInvalid={!!errors.property?.data?.realty?.floorsCount}
                      >
                        <FormLabel tooltip="Do počtu nadzemních podlaží se nezapočítává podkroví.">
                          Počet nadzemních podlaží
                        </FormLabel>

                        <InputGroup
                          placeholder="Např. 1"
                          {...register('property.data.realty.floorsCount')}
                          errors={errors.property?.data?.realty?.floorsCount}
                          isDisabled={isContracted}
                        />
                      </FormControl>

                      <CustomCollapse in={isOpenBasement}>
                        <FormControl
                          isRequired
                          isInvalid={
                            !!errors.property?.data?.realty?.basementArea
                          }
                        >
                          <FormLabel tooltip="Plocha podsklepení">
                            Plocha podsklepení
                          </FormLabel>
                          <Select
                            {...register('property.data.realty.basementArea')}
                            defaultValue={''}
                            isDisabled={isContracted}
                          >
                            <option disabled value="">
                              Vyberte plochu podsklepení
                            </option>
                            {dataBasementArea?.data.map((item) => (
                              <option
                                value={item.id.toString()}
                                key={item.id.toString()}
                              >
                                {item.description}
                              </option>
                            ))}
                          </Select>

                          {errors.property?.data?.realty?.basementArea && (
                            <FormErrorMessage>
                              {
                                errors.property?.data?.realty?.basementArea
                                  .message
                              }
                            </FormErrorMessage>
                          )}
                        </FormControl>
                      </CustomCollapse>

                      <FormControl
                        isInvalid={!!errors.property?.data?.realty?.roofType}
                        isRequired
                      >
                        <FormLabel
                          tooltip={
                            <>
                              Vyberte typ provedení střechy budovy.
                              <br />
                              Plochá střecha - střešní konstrukce nebo střecha
                              se sklonem nanejvýš 5°.
                              <br />
                              Sedlová střecha - všechny ostatní střechy
                              (označované jako např. sedlové, pultové, stanové,
                              mansardové, apod.), které nemůžeme označit jako
                              ploché. V prostoru pod sklonitou střechou se
                              nachází podkroví, které je umístěné nad posledním
                              nadzemním podlažím budovy.
                            </>
                          }
                        >
                          Typ střechy
                        </FormLabel>

                        <RadioGroup
                          name={register('property.data.realty.roofType').name}
                          value={watch(
                            'property.data.realty.roofType',
                          )?.toString()}
                          onChange={(val) =>
                            setValue(
                              'property.data.realty.roofType',
                              Number(val),
                            )
                          }
                          isDisabled={isContracted}
                        >
                          <RadioStack variant="stackedTile">
                            {dataStrechy?.data.map((item) => (
                              <Radio
                                value={item.id.toString()}
                                key={item.id.toString()}
                              >
                                {item.description}
                              </Radio>
                            ))}
                          </RadioStack>
                        </RadioGroup>

                        {errors.property?.data?.realty?.roofType && (
                          <FormErrorMessage>
                            {errors.property?.data?.realty?.roofType.message}
                          </FormErrorMessage>
                        )}
                      </FormControl>

                      <CustomCollapse in={isOpenRoofSedlova}>
                        <FormControl
                          isInvalid={
                            !!errors.property?.data?.realty?.isRoofResidential
                          }
                          isRequired
                        >
                          <FormLabel tooltip="Je podkroví obytné">
                            Je podkroví obytné
                          </FormLabel>

                          <RadioGroup
                            isDisabled={isContracted}
                            value={
                              watch('property.data.realty.isRoofResidential') ==
                                true
                                ? 'yes'
                                : watch(
                                  'property.data.realty.isRoofResidential',
                                ) == false
                                  ? 'no'
                                  : undefined
                            }
                            onChange={(val) =>
                              setValue(
                                'property.data.realty.isRoofResidential',
                                val === 'yes',
                              )
                            }
                          >
                            <RadioStack variant="stackedTile">
                              <Radio value="yes">Ano</Radio>
                              <Radio value="no">Ne</Radio>
                            </RadioStack>
                          </RadioGroup>

                          {errors.property?.data?.realty?.isRoofResidential && (
                            <FormErrorMessage>
                              {
                                errors.property?.data?.realty?.isRoofResidential
                                  .message
                              }
                            </FormErrorMessage>
                          )}
                        </FormControl>
                      </CustomCollapse>

                      <CustomCollapse
                        in={
                          isOpenRoofSedlova &&
                          isOpenRoofNotResidential &&
                          isInsurerAvailable(Insurer.Kooperativa)
                        }
                      >
                        <FormControl
                          isInvalid={!!errors.property?.data?.realty?.atticArea}
                        >
                          <FormLabel tooltip="Pojišťovna Kooperativa nezahrnuje neobytné podkroví do celkové podlahové plochy">
                            Plocha podkroví
                          </FormLabel>

                          <ChakraInputGroup maxW={32}>
                            <Input
                              placeholder="Např. 128"
                              {...register('property.data.realty.atticArea')}
                              isDisabled={isContracted}
                            />
                            <InputRightElement children="㎡" />
                          </ChakraInputGroup>

                          {errors.property?.data?.realty?.atticArea && (
                            <FormErrorMessage>
                              {errors.property?.data?.realty?.atticArea.message}
                            </FormErrorMessage>
                          )}
                        </FormControl>
                      </CustomCollapse>

                      <FormControl
                        isInvalid={!!errors.property?.data?.realty?.finishState}
                        isRequired
                      >
                        <FormLabel
                          tooltip={
                            <Stack gap={2}>
                              <Text>
                                Rozhodujte podle toho, které prvky převažují.
                              </Text>
                              <Text>
                                <Text fontWeight="bold" mr={1}>
                                  Základní provedení
                                </Text>{' '}
                                jednotlivých konstrukcí se vyznačuje nižší
                                kvalitou materiálu, základy jsou bez izolací,
                                chybí elektroinstalace, bleskosvod, rozvody
                                vody, koupelna, WC je mimo budovu.
                              </Text>
                              <Text>
                                <Text fontWeight="bold" mr={1}>
                                  Standardní provedení
                                </Text>{' '}
                                jednotlivých konstrukcí využívá standardní
                                materiály, obsahuje standardní vnitřní vybavení,
                                jako jsou vana či sprchový kout, splachovací
                                záchod.
                              </Text>
                              <Text>
                                <Text fontWeight="bold" mr={1}>
                                  Luxusní provedení
                                </Text>{' '}
                                jednotlivých konstrukcí se vyznačuje zateplenou
                                fasádou a vnějšími obklady, vnitřní vybavení je
                                z exotických a nadstandardních materiálů.
                              </Text>
                            </Stack>
                          }
                        >
                          Provedení budovy
                        </FormLabel>

                        <RadioGroup
                          name={
                            register('property.data.realty.finishState').name
                          }
                          value={watch(
                            'property.data.realty.finishState',
                          )?.toString()}
                          onChange={(val) =>
                            setValue(
                              'property.data.realty.finishState',
                              Number(val),
                            )
                          }
                          isDisabled={isContracted}
                        >
                          <RadioStack variant="stackedTile">
                            {dataDokonceni?.data.map((item) => (
                              <Radio
                                value={item.id.toString()}
                                key={item.id.toString()}
                              >
                                {item.description}
                              </Radio>
                            ))}
                          </RadioStack>
                        </RadioGroup>

                        {errors.property?.data?.realty?.finishState && (
                          <FormErrorMessage>
                            {errors.property?.data?.realty?.finishState.message}
                          </FormErrorMessage>
                        )}
                      </FormControl>
                    </Box>

                    {!isContracted && (
                      <Box>
                        <HouseFloorSelector
                          floors={
                            floorsAboveGroundValue
                              ? parseInt(floorsAboveGroundValue.toString())
                              : 0
                          }
                          basement={!!houseWithBasementValue}
                          roof={residentialRoof ?? false}
                          showRoof={isOpenRoofSedlova}
                          onRoofChange={(selected: boolean) => {
                            setValue(
                              'property.data.realty.isRoofResidential',
                              selected,
                            )
                          }}
                          onFloorsChange={(floors: number) =>
                            setValue('property.data.realty.floorsCount', floors)
                          }
                          onBasementChange={(isSelected: boolean) => {
                            setValue('property.data.hasBasement', isSelected)
                          }}
                        />
                      </Box>
                    )}
                  </Flex>
                </CustomCollapse>

                <CustomCollapse in={isOpenApartment}>
                  <FormSpacer />

                  <Box sx={{ display: 'grid', gap: 4 }}>
                    <FormControl
                      isInvalid={
                        !!errors.property?.data?.realty?.apartmentNumber
                      }
                      isRequired
                    >
                      <FormLabel>Č. bytu</FormLabel>

                      <ChakraInputGroup maxW={48}>
                        <Input
                          placeholder="Např. 513"
                          {...register('property.data.realty.apartmentNumber')}
                          isDisabled={isContracted}
                        />
                      </ChakraInputGroup>

                      {errors.property?.data?.realty?.apartmentNumber && (
                        <FormErrorMessage>
                          {
                            errors.property?.data?.realty?.apartmentNumber
                              .message
                          }
                        </FormErrorMessage>
                      )}
                    </FormControl>

                    <FormGroup>
                      <FormControl>
                        <FormLabel tooltip="Pro domácnost v přízemí, sklepním bytě nebo vyvýšeném přízemí uveďte hodnotu 0, jinak uveďte číslo patra. Pokud je domácnost ve více podlažích, uveďte to nejnižší. Hodnota může být vyžadována pojišťovnou pro určení rizika povodně.">
                          Poschodí
                        </FormLabel>

                        <ChakraInputGroup maxW={48}>
                          <Input
                            placeholder="Např. 5"
                            {...register('property.data.realty.apartmentFloor')}
                            isDisabled={isContracted}
                          />
                        </ChakraInputGroup>
                      </FormControl>

                      {(isInsurerAvailable(Insurer.Axa) ||
                        isInsurerAvailable(
                          Insurer.GeneraliCeskaPojistovna,
                        )) && (
                          <FormControl>
                            <FormLabel tooltip="Zaškrtněte, pokud je byt v posledním poschodí bytového domu">
                              Poslední patro
                            </FormLabel>
                            <SwitchControl>
                              <Switch
                                {...register(
                                  'property.data.realty.isApartmentOnLastFloor',
                                )}
                                isChecked={watch(
                                  'property.data.realty.isApartmentOnLastFloor',
                                )}
                                isDisabled={isContracted}
                              />
                              <FormLabel
                                htmlFor={
                                  'property.data.realty.isApartmentOnLastFloor'
                                }
                                variant="switch"
                              >
                                Je v posledním poschodí
                              </FormLabel>
                            </SwitchControl>
                          </FormControl>
                        )}
                    </FormGroup>
                  </Box>

                  <FormSpacer />
                </CustomCollapse>

                <FormControl
                  isInvalid={!!errors.property?.data?.realty?.structuralType}
                  isRequired
                >
                  <FormLabel
                    tooltip={
                      <>
                        Vyberte typ konstrukce.
                        <br />
                        Pro pojišťovnu UNIQA:
                        <br />
                        Provedení Zdivo a Smíšené se vyznačuje nosnou konstrukcí
                        z nehořlavých či převážně z nehořlavých materiálů (např.
                        cihla, beton, panel) a nehořlavou střechou (např. pálené
                        tašky, plech, eternit, nehořlavý plast apod.). Dřevěnou
                        konstrukcí se rozumím především dřevostavby a stavby z
                        jiných hořlavých materiálů, nebo stavby takového
                        provedení, kde hořlavé konstrukce převládají z více než
                        50 %.
                      </>
                    }
                  >
                    Typ konstrukce
                  </FormLabel>

                  <RadioGroup
                    name={register('property.data.realty.structuralType').name}
                    value={watch(
                      'property.data.realty.structuralType',
                    )?.toString()}
                    onChange={(val) =>
                      setValue(
                        'property.data.realty.structuralType',
                        Number(val),
                      )
                    }
                    isDisabled={isContracted}
                  >
                    <RadioStack variant="stackedTile">
                      {dataTypKonstrukce?.data.map((item) => (
                        <Radio
                          value={item.id.toString()}
                          key={item.id.toString()}
                        >
                          {item.description}
                        </Radio>
                      ))}
                    </RadioStack>
                  </RadioGroup>

                  {errors.property?.data?.realty?.structuralType && (
                    <FormErrorMessage>
                      {errors.property?.data?.realty?.structuralType.message}
                    </FormErrorMessage>
                  )}
                </FormControl>

                <FormControl
                  isInvalid={!!errors.property?.data?.realty?.state}
                  isRequired
                >
                  <FormLabel tooltip="Vyberte stav nemovitosti">
                    Stav nemovitosti
                  </FormLabel>

                  <RadioGroup
                    name={register('property.data.realty.state').name}
                    value={watch('property.data.realty.state')?.toString()}
                    onChange={(val) =>
                      setValue('property.data.realty.state', Number(val))
                    }
                    isDisabled={isContracted}
                  >
                    <RadioStack variant="stackedTile">
                      {dataStav?.data.map((item) => (
                        <Radio
                          value={item.id.toString()}
                          key={item.id.toString()}
                        >
                          {item.description}
                        </Radio>
                      ))}
                    </RadioStack>
                  </RadioGroup>

                  {errors.property?.data?.realty?.state && (
                    <FormErrorMessage>
                      {errors.property?.data?.realty?.state.message}
                    </FormErrorMessage>
                  )}
                </FormControl>

                {isInsurersAvailable([
                  Insurer.Axa,
                  Insurer.CPP,
                  Insurer.Csob,
                  Insurer.Slavia,
                  Insurer.Kooperativa,
                  Insurer.GeneraliCeskaPojistovna,
                  Insurer.Allianz,
                  Insurer.Direct,
                  Insurer.Pillow,
                ]) && (
                    <FormControl
                      isInvalid={!!errors.property?.data?.realty?.goodState}
                      isRequired
                    >
                      <FormLabel tooltip="Vyberte zda je budova v dobrém technickém stavu. Budovy ve špatném technickém stavu není možné pojistit.">
                        Dobrý technický stav
                      </FormLabel>

                      <RadioGroup
                        isDisabled={isContracted}
                        value={
                          watch('property.data.realty.goodState') == true
                            ? 'yes'
                            : watch('property.data.realty.goodState') == false
                              ? 'no'
                              : undefined
                        }
                        onChange={(val) =>
                          setValue(
                            'property.data.realty.goodState',
                            val === 'yes',
                          )
                        }
                      >
                        <RadioStack variant="stackedTile">
                          <Radio value="yes">Ano</Radio>
                          <Radio value="no">Ne</Radio>
                        </RadioStack>
                      </RadioGroup>

                      {errors.property?.data?.realty?.goodState && (
                        <FormErrorMessage>
                          {errors.property?.data?.realty?.goodState.message}
                        </FormErrorMessage>
                      )}
                    </FormControl>
                  )}

                <FormSpacer />

                <FormGroup sx={{ alignItems: 'end' }}>
                  <FormControl
                    isInvalid={!!errors.property?.data?.realty?.realtyAmount}
                    isRequired
                  >
                    <FormLabel tooltip="Zadejte hodnotu nemovitosti. Do hodnoty se započítávají zdi, omítky, podlahy, rozvody, topení, kuchyňská linka, dveře, koupelnové vybavení...">
                      Hodnota nemovitosti
                    </FormLabel>

                    <CustomNumberInput
                      w={48}
                      defaultValue={getValues(
                        'property.data.realty.realtyAmount',
                      )}
                      onValueChange={(_, value) =>
                        setValue('property.data.realty.realtyAmount', value)
                      }
                      isDisabled={isContracted}
                      inputRightElement="Kč"
                      placeholder="Např. 10 000 000"
                    />

                    {errors.property?.data?.realty?.realtyAmount && (
                      <FormErrorMessage>
                        {errors.property?.data?.realty?.realtyAmount.message}
                      </FormErrorMessage>
                    )}
                  </FormControl>

                  <CustomCollapse in={!isOpenApartment}>
                    <Flex sx={{ gap: 4, alignItems: 'center' }}>
                      <Button
                        variant="outline"
                        isDisabled={isContracted}
                        onClick={() => calcRealtyAmount()}
                      >
                        <Calculate color="secondary.500" />
                        Orientační poj. částka
                      </Button>

                      <Tooltip
                        shouldWrapChildren
                        label="Jedná se o orientační pojistnou částku vypočítanou dle obecného vzorce, se započítaným nárůstem cen stavebních prací a materiálů, za kterou neneseme odpovědnost"
                      >
                        <IconButton variant="ghost" aria-label="">
                          <QuestionMark />
                        </IconButton>
                      </Tooltip>

                      {calcRealtyAmountError && (
                        <Text variant={'error'}>{calcRealtyAmountError}</Text>
                      )}
                    </Flex>
                  </CustomCollapse>
                </FormGroup>

                <FormControl
                  isInvalid={!!errors.property?.data?.realty?.secondaryRealty}
                  isRequired
                >
                  <FormLabel tooltip="Vyberte zda chcete pojistit vedlejší budovy. Pokud bude zvoleno NE, budou v nabídkách zobrazeny limity na pojištění vedlejších staveb zdarma dle aktuálních podmínek pojišťoven.">
                    Vedlejší budovy
                  </FormLabel>

                  <RadioGroup
                    value={
                      watch('property.data.realty.secondaryRealty') == true
                        ? 'yes'
                        : watch('property.data.realty.secondaryRealty') == false
                          ? 'no'
                          : undefined
                    }
                    onChange={(val) =>
                      setValue(
                        'property.data.realty.secondaryRealty',
                        val === 'yes',
                      )
                    }
                    isDisabled={isContracted}
                  >
                    <RadioStack variant="stackedTile">
                      <Radio value="yes">Ano</Radio>
                      <Radio value="no">Ne</Radio>
                    </RadioStack>
                  </RadioGroup>

                  {errors.property?.data?.realty?.secondaryRealty && (
                    <FormErrorMessage>
                      {errors.property?.data?.realty?.secondaryRealty.message}
                    </FormErrorMessage>
                  )}
                </FormControl>

                <CustomCollapse in={secondaryRealty == true}>
                  <Box sx={{ display: 'grid', gap: 4 }}>
                    {fieldsSecondaryRealties.map((field, index) => (
                      <React.Fragment key={field.id}>
                        <Divider borderColor="gray" />

                        <Box sx={{ display: 'grid', gap: 4 }}>
                          <FormGroup>
                            <FormControl>
                              <FormLabel>Typ stavby</FormLabel>

                              <Select
                                {...register(
                                  `property.data.secondaryRealties.${index}.type`,
                                )}
                                isDisabled={isContracted}
                              >
                                <option disabled value="">
                                  Vyberte typ stavby
                                </option>
                                {dataBuildingType?.data.map((item) => (
                                  <option
                                    value={item.id.toString()}
                                    key={item.id.toString()}
                                  >
                                    {item.description}
                                  </option>
                                ))}
                              </Select>
                            </FormControl>

                            <FormControl
                              isInvalid={
                                !!(
                                  errors.property?.data?.secondaryRealties &&
                                  errors.property?.data?.secondaryRealties[
                                    index
                                  ]?.secondaryRealtyAmount
                                )
                              }
                              isRequired
                            >
                              <FormLabel>Hodnota nemovitosti</FormLabel>

                              <CustomNumberInput
                                w={48}
                                defaultValue={getValues(
                                  `property.data.secondaryRealties.${index}.secondaryRealtyAmount`,
                                )}
                                onValueChange={(_, value) =>
                                  setValue(
                                    `property.data.secondaryRealties.${index}.secondaryRealtyAmount`,
                                    value,
                                  )
                                }
                                isDisabled={isContracted}
                                inputRightElement="Kč"
                                placeholder="Např. 3 000 000"
                              />

                              {errors.property?.data?.secondaryRealties &&
                                errors.property?.data?.secondaryRealties[index]
                                  ?.secondaryRealtyAmount && (
                                  <FormErrorMessage>
                                    {
                                      errors.property?.data?.secondaryRealties[
                                        index
                                      ]?.secondaryRealtyAmount?.message
                                    }
                                  </FormErrorMessage>
                                )}
                            </FormControl>
                          </FormGroup>

                          <FormGroup sx={{ alignItems: 'end' }}>
                            <FormControl>
                              <FormLabel>Popis</FormLabel>

                              <ChakraInputGroup>
                                <Input
                                  placeholder="Popis"
                                  {...register(
                                    `property.data.secondaryRealties.${index}.description`,
                                  )}
                                  isDisabled={isContracted}
                                />
                              </ChakraInputGroup>
                            </FormControl>

                            {!isContracted &&
                              fieldsSecondaryRealties.length > 1 && (
                                <Flex sx={{ gap: 4, alignItems: 'center' }}>
                                  <Button
                                    variant="outline"
                                    onClick={() => removeSecondaryRealty(index)}
                                  >
                                    <Trash color="secondary.500" />
                                    Odebrat
                                  </Button>

                                  {/* <Tooltip shouldWrapChildren label="Odebrat vedlejší budovu">
                                  <IconButton variant="ghost" aria-label="">
                                    <QuestionMark />
                                  </IconButton>
                                </Tooltip> */}
                                </Flex>
                              )}
                          </FormGroup>
                        </Box>
                      </React.Fragment>
                    ))}

                    <Divider borderColor="gray" />

                    {!isContracted && (
                      <Box>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() =>
                            appendSecondaryRealty({} as SecondaryRealtyDto)
                          }
                        >
                          Přidat další budovu
                        </Button>
                      </Box>
                    )}
                  </Box>
                </CustomCollapse>
              </FormSectionBody>
            </FormSection>
          </CustomCollapse>

          <CustomCollapse in={isOpenHousehold}>
            <FormSection>
              <FormSectionHeader>
                <FormSectionTitle>Domácnost</FormSectionTitle>

                <FormSectionDescription>Trvale obývaná.</FormSectionDescription>
              </FormSectionHeader>

              <FormSectionBody>
                <FormGroup>
                  <FormControl
                    isInvalid={!!errors.property?.data?.household?.floor}
                    isRequired
                  >
                    <FormLabel tooltip="Pro domácnost v přízemí, sklepním bytě nebo vyvýšeném přízemí uveďte hodnotu 0, jinak uveďte číslo patra. Pokud je domácnost ve více podlažích, uveďte to nejnižší. Hodnota může být vyžadována pojišťovnou pro určení rizika povodně.">
                      Poschodí
                    </FormLabel>

                    <InputGroup
                      htmlSize={8}
                      maxW={32}
                      placeholder="Např. 1"
                      {...register('property.data.household.floor')}
                      errors={errors.property?.data?.household?.floor}
                      isDisabled={isContracted}
                    />
                  </FormControl>

                  {(isInsurerAvailable(Insurer.Axa) ||
                    isInsurerAvailable(Insurer.GeneraliCeskaPojistovna)) && (
                      <CustomCollapse in={isOpenApartment}>
                        <FormControl>
                          <FormLabel tooltip="Zaškrtněte, pokud je byt v posledním poschodí bytového domu">
                            Poslední patro
                          </FormLabel>
                          <SwitchControl>
                            <Switch
                              {...register(
                                'property.data.household.isApartmentOnLastFloor',
                              )}
                              isChecked={watch(
                                'property.data.household.isApartmentOnLastFloor',
                              )}
                              isDisabled={isContracted}
                            />
                            <FormLabel
                              htmlFor={
                                'property.data.household.isApartmentOnLastFloor'
                              }
                              variant="switch"
                            >
                              Je v posledním poschodí
                            </FormLabel>
                          </SwitchControl>
                        </FormControl>
                      </CustomCollapse>
                    )}
                </FormGroup>

                <FormControl
                  isInvalid={!!errors.property?.data?.household?.livingArea}
                  isRequired
                >
                  <FormLabel tooltip="Celková započitatelná plocha bytu je podlahová plocha bytu včetně kuchyně, chodeb, WC, koupelny a schodiště. Do této plochy se započítávají taky plochy balkonů, lodžií, teras, plochy ostatních příslušenství bytu a nebytových prostor.">
                    Podlahová plocha
                  </FormLabel>

                  <CustomNumberInput
                    w={48}
                    defaultValue={getValues(
                      `property.data.household.livingArea`,
                    )}
                    onValueChange={(_, value) =>
                      setValue(`property.data.household.livingArea`, value)
                    }
                    isDisabled={isContracted}
                    inputRightElement="㎡"
                    placeholder="Např. 128"
                  />

                  {errors.property?.data?.household?.livingArea && (
                    <FormErrorMessage>
                      {errors.property?.data?.household?.livingArea.message}
                    </FormErrorMessage>
                  )}
                </FormControl>

                <FormControl>
                  <FormLabel tooltip="Napište číslo bytové jednotky číslicí. Pro byt v rodinném domě uveďte číslici 1 nebo 2 nebo 3.">
                    Číslo bytu
                  </FormLabel>

                  <InputGroup
                    htmlSize={8}
                    maxW={32}
                    placeholder="Např. 1"
                    {...register('property.data.household.apartmentNumber')}
                    isDisabled={isContracted}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel tooltip="Zadejte počet osob v domácnosti">
                    Počet osob v domácnosti
                  </FormLabel>

                  <InputGroup
                    htmlSize={3}
                    placeholder="Např. 5"
                    {...register('property.data.household.countOfPeople')}
                    isDisabled={isContracted}
                  />
                </FormControl>

                {isInsurerAvailable(Insurer.Pillow) && (
                  <>
                    <CustomCollapse in={!isOpenRealty}>
                      <FormSpacer />
                    </CustomCollapse>

                    <CustomCollapse in={!isOpenRealty}>
                      <FormControl
                        isInvalid={
                          !!errors.property?.data?.household?.approvedYear
                        }
                      >
                        <FormLabel tooltip="Pokud je stavba zkolaudována, uveďte rok kolaudace. Pokud koladudace nebyla nutná uveďte rok výstavby.">
                          Rok kolaudace či výstavby
                        </FormLabel>

                        <InputGroup
                          htmlSize={4}
                          {...register('property.data.household.approvedYear')}
                          errors={
                            errors.property?.data?.household?.approvedYear
                          }
                          isDisabled={isContracted}
                        />
                      </FormControl>
                    </CustomCollapse>

                    <CustomCollapse in={!isOpenRealty}>
                      <FormControl
                        isInvalid={
                          !!errors.property?.data?.household?.structuralType
                        }
                      >
                        <FormLabel tooltip="Typ konstrukce">
                          Typ konstrukce
                        </FormLabel>

                        <RadioGroup
                          name={
                            register('property.data.household.structuralType')
                              .name
                          }
                          value={watch(
                            'property.data.household.structuralType',
                          )?.toString()}
                          onChange={(val) =>
                            setValue(
                              'property.data.household.structuralType',
                              Number(val),
                            )
                          }
                          isDisabled={isContracted}
                        >
                          <RadioStack variant="stackedTile">
                            {dataTypKonstrukce?.data.map((item) => (
                              <Radio
                                value={item.id.toString()}
                                key={item.id.toString()}
                              >
                                {item.description}
                              </Radio>
                            ))}
                          </RadioStack>
                        </RadioGroup>

                        {errors.property?.data?.household?.structuralType && (
                          <FormErrorMessage>
                            {
                              errors.property?.data?.household?.structuralType
                                .message
                            }
                          </FormErrorMessage>
                        )}
                      </FormControl>
                    </CustomCollapse>

                    <CustomCollapse in={!isOpenRealty && !isOpenApartment}>
                      <FormSpacer />

                      <Flex
                        sx={{
                          columnGap: 32,
                          rowGap: 8,
                          placeItems: 'start',
                          flexWrap: 'wrap',
                        }}
                      >
                        <Box sx={{ display: 'grid', gap: 4 }}>
                          <FormControl
                            isInvalid={
                              !!errors.property?.data?.household?.floorsCount
                            }
                          >
                            <FormLabel tooltip="Počet nadzemních podlaží">
                              Počet nadzemních podlaží
                            </FormLabel>

                            <InputGroup
                              placeholder="Např. 1"
                              {...register(
                                'property.data.household.floorsCount',
                              )}
                              errors={
                                errors.property?.data?.household?.floorsCount
                              }
                              isDisabled={isContracted}
                            />
                          </FormControl>

                          <FormControl
                            isInvalid={
                              !!errors.property?.data?.household?.roofType
                            }
                          >
                            <FormLabel tooltip="Typ střechy">
                              Typ střechy
                            </FormLabel>

                            <RadioGroup
                              name={
                                register('property.data.household.roofType')
                                  .name
                              }
                              value={watch(
                                'property.data.household.roofType',
                              )?.toString()}
                              onChange={(val) =>
                                setValue(
                                  'property.data.household.roofType',
                                  Number(val),
                                )
                              }
                              isDisabled={isContracted}
                            >
                              <RadioStack variant="stackedTile">
                                {dataStrechy?.data.map((item) => (
                                  <Radio
                                    value={item.id.toString()}
                                    key={item.id.toString()}
                                  >
                                    {item.description}
                                  </Radio>
                                ))}
                              </RadioStack>
                            </RadioGroup>

                            {errors.property?.data?.household?.roofType && (
                              <FormErrorMessage>
                                {
                                  errors.property?.data?.household?.roofType
                                    .message
                                }
                              </FormErrorMessage>
                            )}
                          </FormControl>

                          <CustomCollapse in={isOpenHouseholdRoofSedlova}>
                            <FormControl
                              isInvalid={
                                !!errors.property?.data?.household
                                  ?.isRoofResidential
                              }
                            >
                              <FormLabel tooltip="Je podkroví obytné">
                                Je podkroví obytné
                              </FormLabel>

                              <RadioGroup
                                value={
                                  watch(
                                    'property.data.household.isRoofResidential',
                                  ) == true
                                    ? 'yes'
                                    : watch(
                                      'property.data.household.isRoofResidential',
                                    ) == false
                                      ? 'no'
                                      : undefined
                                }
                                onChange={(val) =>
                                  setValue(
                                    'property.data.household.isRoofResidential',
                                    val === 'yes',
                                  )
                                }
                                isDisabled={isContracted}
                              >
                                <RadioStack variant="stackedTile">
                                  <Radio value="yes">Ano</Radio>
                                  <Radio value="no">Ne</Radio>
                                </RadioStack>
                              </RadioGroup>

                              {errors.property?.data?.household
                                ?.isRoofResidential && (
                                  <FormErrorMessage>
                                    {
                                      errors.property?.data?.household
                                        ?.isRoofResidential.message
                                    }
                                  </FormErrorMessage>
                                )}
                            </FormControl>
                          </CustomCollapse>
                        </Box>

                        {!isContracted && (
                          <Box sx={{ display: 'grid', gap: 4 }}>
                            {/* <FormLabel>Obývaná podlaží</FormLabel> */}

                            <HouseFloorSelector
                              floors={
                                householdNumberOfFloorsValue
                                  ? parseInt(
                                    householdNumberOfFloorsValue.toString(),
                                  )
                                  : 0
                              }
                              basement={!!houseWithBasementValue}
                              roof={
                                watch(
                                  'property.data.household.isRoofResidential',
                                ) ?? false
                              }
                              showRoof={isOpenHouseholdRoofSedlova}
                              onRoofChange={(selected: boolean) => {
                                setValue(
                                  'property.data.household.isRoofResidential',
                                  selected,
                                )
                              }}
                              onFloorsChange={(floors: number) =>
                                setValue(
                                  'property.data.household.floorsCount',
                                  floors,
                                )
                              }
                              onBasementChange={(isSelected: boolean) => {
                                // setValue(
                                //   'property.data.household.floor',
                                //   (householdNumberOfFloorsValue ?? 1) + (isSelected ? 1 : 0),
                                // )
                                setValue(
                                  'property.data.hasBasement',
                                  isSelected,
                                )
                              }}
                            />
                          </Box>
                        )}
                      </Flex>
                    </CustomCollapse>

                    <CustomCollapse in={!isOpenRealty && isOpenApartment}>
                      <FormSpacer />

                      <FormControl
                        isInvalid={!!errors.property?.data?.household?.owner}
                      >
                        <FormLabel>Vlastnictví</FormLabel>

                        <RadioGroup
                          value={watch(
                            'property.data.household.owner',
                          )?.toString()}
                          onChange={(x) =>
                            setValue(
                              'property.data.household.owner',
                              parseInt(x),
                            )
                          }
                          isDisabled={isContracted}
                        >
                          <RadioStack variant="stackedTile">
                            {dataOwner?.data.map((item) => (
                              <Radio
                                value={item.id.toString()}
                                key={item.id.toString()}
                              >
                                {item.description}
                              </Radio>
                            ))}
                          </RadioStack>
                        </RadioGroup>

                        {errors.property?.data?.household?.owner && (
                          <FormErrorMessage>
                            {errors.property?.data?.household?.owner.message}
                          </FormErrorMessage>
                        )}
                      </FormControl>
                    </CustomCollapse>
                  </>
                )}

                <FormSpacer />

                <FormControl
                  isInvalid={!!errors.property?.data?.household?.equipment}
                  isRequired
                >
                  <FormLabel
                    tooltip={
                      <>
                        Základní - domácnost je převážně vybavena základním
                        zařízením pořízeným např. v bazaru a oblečení a obuv
                        jsou nakupovány převážně v asijských tržnicích.
                        <br />
                        Standardní - domácnost je převážně vybavena standardním
                        zařízením a oblečením a obuví, které jsou běžně dostupné
                        na našem trhu (sériová výroba - např. IKEA, Sconto,
                        Baťa, Hervis).
                        <br />
                        Luxusní - domácnost je převážně vybavena luxusním
                        zařízením nebo zařízením pořízeným na zakázku, oblečení
                        i obuv jsou převážně značkové."
                      </>
                    }
                  >
                    Vybavení
                  </FormLabel>

                  <RadioGroup
                    name={register('property.data.household.equipment').name}
                    value={watch(
                      'property.data.household.equipment',
                    )?.toString()}
                    onChange={(val) =>
                      setValue('property.data.household.equipment', Number(val))
                    }
                    isDisabled={isContracted}
                  >
                    <RadioStack variant="stackedTile">
                      {dataVybaveni?.data.map((item) => (
                        <Radio
                          value={item.id.toString()}
                          key={item.id.toString()}
                        >
                          {item.description}
                        </Radio>
                      ))}
                    </RadioStack>
                  </RadioGroup>

                  {errors.property?.data?.household?.equipment && (
                    <FormErrorMessage>
                      {errors.property?.data?.household?.equipment.message}
                    </FormErrorMessage>
                  )}
                </FormControl>

                <FormGroup sx={{ alignItems: 'end' }}>
                  <FormControl
                    isInvalid={!!errors.property?.data?.household?.amount}
                    isRequired
                  >
                    <FormLabel tooltip="Nábytek, elektronika a spotřebiče, koberce, oblečení, hračky, nádobí, sportovní potřeby, kosmetika…">
                      Vybavení domácnosti
                    </FormLabel>

                    <CustomNumberInput
                      w={48}
                      defaultValue={getValues(`property.data.household.amount`)}
                      onValueChange={(_, value) =>
                        setValue(`property.data.household.amount`, value)
                      }
                      isDisabled={isContracted}
                      inputRightElement="Kč"
                      placeholder="Např. 10 000 000"
                    />

                    {errors.property?.data?.household?.amount && (
                      <FormErrorMessage>
                        {errors.property?.data?.household?.amount.message}
                      </FormErrorMessage>
                    )}
                  </FormControl>

                  <Flex sx={{ gap: 4, alignItems: 'center' }}>
                    <Button
                      variant="outline"
                      isDisabled={isContracted}
                      onClick={() => calcHouseholdAmount()}
                    >
                      <Calculate color="secondary.500" />
                      Orientační poj. částka
                    </Button>

                    <Tooltip
                      shouldWrapChildren
                      label="Jedná se o orientační pojistnou částku vypočítanou dle obecného vzorce, se započítaným nárůstem cen stavebních prací a materiálů, za kterou neneseme odpovědnost"
                    >
                      <IconButton variant="ghost" aria-label="">
                        <QuestionMark />
                      </IconButton>
                    </Tooltip>

                    {calcHouseholdAmountError && (
                      <Text variant={'error'}>{calcHouseholdAmountError}</Text>
                    )}
                  </Flex>
                </FormGroup>
              </FormSectionBody>
            </FormSection>
          </CustomCollapse>

          <FormSection>
            <FormSectionHeader>
              <FormSectionTitle>Informace o pojistníkovi</FormSectionTitle>

              <FormSectionDescription>
                Zadejte informace o pojistníkovi.
              </FormSectionDescription>
            </FormSectionHeader>

            <FormSectionBody>
              <InsureeType
                personPrefix={phnameof((x) => x.person)}
                disabled={isContracted}
              />

              <FormSpacer />

              {[Selection.Person, Selection.SelfEmployed].includes(phType) && (
                <BCNOptions
                  personPrefix={phnameof((x) => x.person)}
                  disabled={isContracted}
                />
              )}
              {[Selection.Company, Selection.SelfEmployed].includes(phType) && (
                <ICOOptions
                  personPrefix={phnameof((x) => x.person)}
                  disabled={isContracted}
                />
              )}

              <FormGroup>
                {[Selection.Person, Selection.SelfEmployed].includes(
                  phType,
                ) && (
                    <InsureeName
                      personPrefix={phnameof((x) => x.person)}
                      showTitles
                      isDisabled={isContracted}
                    />
                  )}
              </FormGroup>

              <FormSpacer />

              <FormControl>
                <FormLabel tooltip="Určení vztahu k předmětu pojištění a také způsobu, jak je majetek užíván.">
                  Vztah k předmětu pojištění
                </FormLabel>

                <RadioGroup
                  name={register('property.data.propertyRelationId').name}
                  value={watch('property.data.propertyRelationId')?.toString()}
                  onChange={(val) =>
                    setValue('property.data.propertyRelationId', Number(val))
                  }
                  isDisabled={isContracted}
                >
                  <RadioStack variant="stackedTile">
                    {dataVztah?.data.map((item) => (
                      <Radio
                        value={item.id.toString()}
                        key={item.id.toString()}
                      >
                        {item.description}
                      </Radio>
                    ))}
                  </RadioStack>
                </RadioGroup>
              </FormControl>

              <CustomCollapse
                in={
                  isInsurerAvailable(Insurer.Pillow) &&
                  propRelationId == Selection.Pronajimatel
                }
              >
                <FormControl
                  isRequired
                  isInvalid={!!errors.property?.data?.longTimeRent}
                >
                  <FormLabel>Je majetek pronajímán dlouhodobě?</FormLabel>

                  <RadioGroup
                    isDisabled={isContracted}
                    value={
                      watch('property.data.longTimeRent') == true
                        ? 'yes'
                        : watch('property.data.longTimeRent') == false
                          ? 'no'
                          : 'yes'
                    }
                    onChange={(val) =>
                      setValue('property.data.longTimeRent', val === 'yes')
                    }
                  >
                    <RadioStack variant="stackedTile">
                      <Radio value="yes">Ano</Radio>
                      <Radio value="no">Ne</Radio>
                    </RadioStack>
                  </RadioGroup>

                  {errors.property?.data?.longTimeRent && (
                    <FormErrorMessage>
                      {errors.property?.data?.longTimeRent.message}
                    </FormErrorMessage>
                  )}
                </FormControl>
              </CustomCollapse>

              <CustomCollapse
                in={
                  isInsurerAvailable(Insurer.GeneraliCeskaPojistovna) &&
                  [Selection.Company, Selection.SelfEmployed].includes(phType)
                }
              >
                <FormSpacer />

                <FormGroup>
                  <Box>
                    <Text fontWeight="bold" mr={1}>
                      Splňuje pojistník alespoň 2 ze 3 níže uvedených podmínek?
                    </Text>
                    <List>
                      <ListItem>
                        Je čistý obrat min. 13 600 000 EUR (cca 340 000 000 Kč)?
                      </ListItem>
                      <ListItem>
                        Je úhrn rozvahy min. 6 600 000 EUR (cca 165 000 000 Kč)?
                      </ListItem>
                      <ListItem>
                        Je průměrný roční stav zaměstnanců min. 250?
                      </ListItem>
                    </List>
                  </Box>

                  <FormControl
                    isInvalid={!!errors.property?.data?.bigRiskCompany}
                  >
                    <RadioGroup
                      value={
                        watch('property.data.bigRiskCompany') == true
                          ? 'yes'
                          : watch('property.data.bigRiskCompany') == false
                            ? 'no'
                            : undefined
                      }
                      onChange={(val) =>
                        setValue('property.data.bigRiskCompany', val === 'yes')
                      }
                      isDisabled={isContracted}
                    >
                      <RadioStack variant="stackedTile">
                        <Radio value="yes">Ano</Radio>
                        <Radio value="no">Ne</Radio>
                      </RadioStack>
                    </RadioGroup>

                    {errors.property?.data?.bigRiskCompany && (
                      <FormErrorMessage>
                        {errors.property?.data?.bigRiskCompany.message}
                      </FormErrorMessage>
                    )}
                  </FormControl>
                </FormGroup>
              </CustomCollapse>

              <FormSpacer />

              {/* InsuredPerson */}
              <FormControl>
                <FormLabel>Pojištěný</FormLabel>

                <Flex gap={8}>
                  <Controller
                    name="policyHolder.insuredPersonSameAs"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <RadioGroup
                        onChange={(e) => onChange(!e ? e : parseInt(e))}
                        value={value?.toString() ?? ''}
                        isDisabled={isContracted}
                      >
                        <RadioStack variant="stackedTile">
                          {sameAsOptions.map((item) => (
                            <Radio
                              key={item.value}
                              value={item.value?.toString() ?? ''}
                            >
                              {item.label}
                            </Radio>
                          ))}
                        </RadioStack>
                      </RadioGroup>
                    )}
                  />
                  <KNButton />
                </Flex>
              </FormControl>

              <CustomCollapse in={!insuredPersonSameAs}>
                <Panel>
                  <InsureeType
                    personPrefix={phnameof((x) => x.insuredPerson)}
                    disabled={isContracted}
                  />

                  <FormSpacer />

                  {[Selection.Person, Selection.SelfEmployed].includes(
                    insuredPersonType,
                  ) && (
                      <BCNOptions
                        personPrefix={phnameof((x) => x.insuredPerson)}
                        disabled={isContracted}
                      />
                    )}
                  {[Selection.Company, Selection.SelfEmployed].includes(
                    insuredPersonType,
                  ) && (
                      <ICOOptions
                        personPrefix={phnameof((x) => x.insuredPerson)}
                        disabled={isContracted}
                      />
                    )}

                  {[Selection.Person, Selection.SelfEmployed].includes(
                    insuredPersonType,
                  ) && (
                      <>
                        <FormGroup>
                          <InsureeName
                            personPrefix={phnameof((x) => x.insuredPerson)}
                            isDisabled={isContracted}
                            showTitles
                          />
                        </FormGroup>
                      </>
                    )}
                </Panel>
              </CustomCollapse>
            </FormSectionBody>
          </FormSection>

          <ErrorSummary errors={errors} />

          {!isContracted && (
            <FormButtons
              rightButtons={
                <>
                  <Button
                    variant="outline"
                    disabled={
                      isSubmitting || isSubmittingNext || isSubmittingCurrent
                    }
                    isLoading={isSubmittingCurrent}
                    loadingText="Ukládám..."
                    type="button"
                    onClick={() => {
                      setValue('nextPage', 'current')
                      setIsSubmittingCurrent(true)
                      submitForm()
                    }}
                  >
                    Uložit
                  </Button>

                  <Button
                    colorScheme="primary"
                    isLoading={isSubmittingNext}
                    disabled={
                      isSubmitting || isSubmittingNext || isSubmittingCurrent
                    }
                    loadingText="Ukládám..."
                    type="button"
                    onClick={() => {
                      setValue('nextPage', 'next')
                      setIsSubmittingNext(true)
                      submitForm()
                    }}
                  >
                    <Check />
                    Uložit a pokračovat
                  </Button>

                  <SubmitDialog
                    isOpen={isSavedFormAlertOpen}
                    onClose={savedFormAlertOnClose}
                    colorScheme={!isSubmitError ? 'primary' : 'red'}
                    header={!isSubmitError ? 'Uloženo' : 'Chyba'}
                    body={
                      !isSubmitError
                        ? !insuranceParamsId
                          ? 'Nabídka byla uložena do rozpracovaných kalkulací'
                          : 'Nabídka byla úspěšně aktualizována'
                        : 'Při ukládání došlo k chybě'
                    }
                  />
                </>
              }
            />
          )}
        </MainContent>
      </form>
    </FormProvider>
  )
}

const KNButton = () => (
  <Button
    variant="outline"
    as={Link}
    to="https://nahlizenidokn.cuzk.cz/"
    target="_blank"
  >
    <Search color="secondary.500" /> Náhled do katastru nemovitostí
  </Button>
)
